import { ulid, type AuthSession } from '@walkkit/shared';
import { ApiError } from './errors';
import { toStoredSession, type SessionStore } from './session';

export type QueryValue = string | number | boolean | null | undefined;

export type RequestOptions = {
  query?: Record<string, QueryValue> | undefined;
  body?: unknown;
  /** Send the bearer token (default true). Public token pages pass false. */
  auth?: boolean;
  /** Send X-Business-Id (default true when authenticated). */
  tenant?: boolean;
  /** Override the auto-generated key (mutations only). */
  idempotencyKey?: string;
  signal?: AbortSignal | undefined;
  headers?: Record<string, string>;
};

export type ApiClient = {
  request<T>(method: string, path: string, opts?: RequestOptions): Promise<T>;
  get<T>(path: string, opts?: Omit<RequestOptions, 'body'>): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  del<T>(path: string, opts?: RequestOptions): Promise<T>;
  /** Exchange the refresh token once, shared by every concurrent caller. */
  refresh(): Promise<RefreshOutcome>;
  store: SessionStore;
};

export type RefreshOutcome = 'ok' | 'rejected' | 'network';

export type ApiClientOptions = {
  /** Origin of the API ('' = same origin). Paths are prefixed with /v1. */
  baseUrl: string;
  store: SessionStore;
  fetch?: typeof fetch;
  /** Called after the session is dropped because refresh failed. */
  onAuthLost?: () => void;
  newIdempotencyKey?: () => string;
  now?: () => number;
};

/** Refresh this long before the access token's stated expiry. */
const EXPIRY_SKEW_MS = 20_000;

export function buildUrl(baseUrl: string, path: string, query?: Record<string, QueryValue>): string {
  const p = path.startsWith('/v1/') || path === '/v1' ? path : `/v1${path.startsWith('/') ? '' : '/'}${path}`;
  let url = `${baseUrl.replace(/\/+$/, '')}${p}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  return url;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const { store } = opts;
  const doFetch: typeof fetch = opts.fetch ?? ((...args) => globalThis.fetch(...args));
  const newKey = opts.newIdempotencyKey ?? (() => ulid());
  const now = opts.now ?? (() => Date.now());
  let refreshInFlight: Promise<RefreshOutcome> | null = null;

  function loseSession() {
    if (store.get()) {
      store.set(null);
      opts.onAuthLost?.();
    }
  }

  function refresh(): Promise<RefreshOutcome> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async (): Promise<RefreshOutcome> => {
      const current = store.get();
      if (!current) return 'rejected';
      let res: Response;
      try {
        res = await doFetch(buildUrl(opts.baseUrl, '/auth/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refreshToken: current.refreshToken }),
        });
      } catch {
        return 'network';
      }
      if (!res.ok) return res.status >= 500 ? 'network' : 'rejected';
      try {
        const next = (await res.json()) as AuthSession;
        store.set(toStoredSession(next, store.get()?.activeBusinessId ?? current.activeBusinessId));
        return 'ok';
      } catch {
        return 'rejected';
      }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function request<T>(method: string, path: string, o: RequestOptions = {}): Promise<T> {
    const upper = method.toUpperCase();
    const useAuth = o.auth !== false;
    const isMutation = upper !== 'GET' && upper !== 'HEAD';
    // One key for the whole logical call, so the post-refresh retry replays safely.
    const idempotencyKey = isMutation ? (o.idempotencyKey ?? newKey()) : undefined;
    const url = buildUrl(opts.baseUrl, path, o.query);

    if (useAuth) {
      const s = store.get();
      if (s && Date.parse(s.accessExpiresAt) - EXPIRY_SKEW_MS <= now()) {
        const outcome = await refresh();
        if (outcome === 'rejected') loseSession();
      }
    }

    const send = (token: string | undefined): Promise<Response> => {
      const headers: Record<string, string> = { Accept: 'application/json, application/problem+json', ...o.headers };
      let body: BodyInit | undefined;
      if (o.body !== undefined) {
        if (o.body instanceof FormData || o.body instanceof Blob) body = o.body;
        else {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(o.body);
        }
      }
      if (token) headers.Authorization = `Bearer ${token}`;
      const businessId = store.get()?.activeBusinessId;
      if (useAuth && o.tenant !== false && businessId) headers['X-Business-Id'] = businessId;
      if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
      const init: RequestInit = { method: upper, headers };
      if (body !== undefined) init.body = body;
      if (o.signal) init.signal = o.signal;
      return doFetch(url, init);
    };

    const tokenUsed = useAuth ? store.get()?.accessToken : undefined;
    let res: Response;
    try {
      res = await send(tokenUsed);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw ApiError.network(e);
    }

    if (res.status === 401 && useAuth && tokenUsed) {
      // Another request may already have rotated the token; only refresh if not.
      const latest = store.get()?.accessToken;
      const outcome: RefreshOutcome = latest && latest !== tokenUsed ? 'ok' : await refresh();
      if (outcome === 'network') throw ApiError.network();
      if (outcome === 'ok') {
        try {
          res = await send(store.get()?.accessToken);
        } catch (e) {
          throw ApiError.network(e);
        }
      }
      if (outcome === 'rejected' || res.status === 401) {
        loseSession();
      }
    }

    if (!res.ok) throw await ApiError.fromResponse(res);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as T;
    }
  }

  return {
    store,
    refresh,
    request,
    get: (path, o) => request('GET', path, o),
    post: (path, body, o) => request('POST', path, { ...o, body }),
    patch: (path, body, o) => request('PATCH', path, { ...o, body }),
    put: (path, body, o) => request('PUT', path, { ...o, body }),
    del: (path, o) => request('DELETE', path, o),
  };
}
